import React from 'react';
import { Box, Heading } from '@wix/design-system';
import {
  type SelectButtonProps,
  NonBlockerWrapper,
  ProductDescription,
  useAnonymous,
  useCurrentModule,
  Base44VoucherCard,
  shouldShowBase44Voucher,
  useAiRecommendationEnabled,
} from '@wix/package-picker-shared-eclipse';
import { ProductCardList } from '@wix/premium-purchase-platform/@components';
import { FeatureList } from './FeatureList';
import { EclipsePrice } from '../../EclipsePrice/EclipsePrice';
import { AiPlanRecommendationCard } from '../../AiPlanRecommendationCard';
import s from './ProductCard.scss';

interface CardContentProps {
  name?: string;
  isRecommended?: boolean;
  SelectButton: React.FC<SelectButtonProps>;
  productId: string;
}

export const CardContent = ({
  name,
  isRecommended,
  SelectButton,
  productId,
}: CardContentProps) => {
  const { isAnonymous } = useAnonymous();
  const module = useCurrentModule();
  const { experiments } = module.internals.experiments.hooks.useExperiments();
  const { isEnabled: isAiRecommendationEnabled } = useAiRecommendationEnabled();
  const { data: product } =
    module.SDK.eclipseOfferingApiClient.hooks.useFetchProduct({ productId });

  const showBase44Voucher = shouldShowBase44Voucher({
    productFamilyId: product?.productFamilyId,
    isExperimentEnabled: experiments.isEnabled('eclipseBase44Voucher'),
  });

  return (
    <>
      <ProductCardList.Default.Card.Header
        height="50px"
        width="100%"
        direction="vertical"
        marginBottom="16px"
        align="left"
      >
        <Heading size="medium" className={s.planTitle}>
          {name}
        </Heading>
        <NonBlockerWrapper componentName="ProductDescription">
          <ProductDescription productId={productId} />
        </NonBlockerWrapper>
      </ProductCardList.Default.Card.Header>
      <ProductCardList.Default.Card.Price
        align="left"
        direction="vertical"
        gap="3px"
      >
        <Box marginBottom="SP3" align="left">
          <EclipsePrice productId={productId} />
        </Box>
      </ProductCardList.Default.Card.Price>
      {!isAnonymous && (
        <Box
          marginBottom="SP3"
          width="100%"
          direction="vertical"
          className={isAiRecommendationEnabled ? s.ctaReveal : undefined}
        >
          <SelectButton
            variant={isRecommended ? 'primary' : 'secondary'}
            fullWidth={true}
          />
        </Box>
      )}
      {isAiRecommendationEnabled && isRecommended && <AiPlanRecommendationCard />}
      <FeatureList productId={productId} />

      {showBase44Voucher && <Base44VoucherCard productId={productId} />}
    </>
  );
};
